import { createAction, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@store/store';
import { message } from 'antd';
import type grapesjs from 'grapesjs';
import * as api from '../api/cmsPages.ts';
import { ensureCssString } from '../grapes/utils.tsx';
import { extractCmsImageIds } from '../utils/assets.tsx';
import { ensureTemplateMetadata } from '../grapes/templateMetadata.ts';
import type { EditorTraitMode } from '../templates/simpleModeConfig.ts';
import type { LegacyEditableDocument } from '../legacy/legacyEditableBlocks.ts';

interface EditorState {
  id?: number;
  slug: string;
  title: string;
  html: string;
  css: string;
  js: string;
  blocks: Record<string, unknown> | null;
  status: 'draft' | 'published';
  isDirty: boolean;
  isSaving: boolean;
  error: string | null;
  lastFetchStatus: number | null;
  imageIds: number[];
  traitMode: EditorTraitMode;
}

const TRAIT_MODE_STORAGE_KEY = 'cms.blocks.mode';

const getStoredTraitMode = (): EditorTraitMode => {
  if (typeof window === 'undefined') {
    return 'simple';
  }
  const stored = window.localStorage.getItem(TRAIT_MODE_STORAGE_KEY);
  return stored === 'advanced' ? 'advanced' : 'simple';
};

export const persistTraitModePreference = (mode: EditorTraitMode) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(TRAIT_MODE_STORAGE_KEY, mode);
  } catch (err) {
    console.warn('Unable to persist CMS trait mode preference', err);
  }
};

const initialState: EditorState = {
  slug: '',
  title: '',
  html: '',
  css: '',
  js: '',
  blocks: null,
  status: 'draft',
  isDirty: false,
  isSaving: false,
  error: null,
  lastFetchStatus: null,
  imageIds: [],
  traitMode: getStoredTraitMode(),
};

let editorInstance: grapesjs.Editor | null = null;
export const setGrapesEditor = (ed: grapesjs.Editor | null) => {
  editorInstance = ed;
};
export const getGrapesEditor = () => editorInstance;

export const recordLoadStatus = createAction<number | null>('cms/editor/recordLoadStatus');

export const loadPage = createAsyncThunk<
  api.PageOut | { slug: string },
  string,
  { rejectValue: string }
>('cms/loadPage', async (slug, { rejectWithValue, dispatch }) => {
  try {
    let status: number | null = null;
    const data = await api.getBySlug(slug, {
      includeDraft: true,
      onHttpMeta: (meta) => {
        status = meta.status ?? null;
      },
    });
    dispatch(recordLoadStatus(status ?? 200));
    const editor = getGrapesEditor();
    if (editor) {
      const component = editor.setComponents(data.html ?? '', {});
      editor.setStyle(data.css ?? '');
      if (data.js) {
        const comps = Array.isArray(component) ? component : [component];
        comps.forEach((c) => {
          (c as unknown as { addScript?: (s: string) => void }).addScript?.(
            data.js as string,
          );
        });
      }
      ensureTemplateMetadata(editor);
    }
    return data;
  } catch (e: any) {  // eslint-disable-line @typescript-eslint/no-explicit-any
    const status = e?.response?.status as number | undefined;
    dispatch(recordLoadStatus(status ?? null));
    if (status === 404) {
      return { slug };
    }
    return rejectWithValue(e?.response?.data?.detail || 'Error loading page');
  }
});

export const savePage = createAsyncThunk<
  api.PageOut,
  void,
  { state: RootState; rejectValue: string }
>('cms/savePage', async (_, { getState, rejectWithValue }) => {
  const editor = getGrapesEditor();
  if (!editor) {
    return rejectWithValue('Editor not ready');
  }
  const html = editor.getHtml();
  const css = ensureCssString(editor.getCss());
  const js = editor.getJs();
  const state = getState().editor;
  const payload: api.PageCreate = {
    slug: state.slug,
    title: state.title,
    html,
    css,
    js,
    blocks: state.blocks,
    is_published: state.status === 'published',
    image_ids: extractCmsImageIds(html),
  };
  try {
    const data = state.id
      ? await api.update(state.id, payload)
      : await api.create(payload);
    message.success('Página guardada');
    return data;
  } catch (e: any) {  // eslint-disable-line @typescript-eslint/no-explicit-any
    message.error('Error al guardar página');
    return rejectWithValue(e?.response?.data?.detail || 'Error saving');
  }
});

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    setFromEditor(state, action: PayloadAction<{ html: string; css: string; js: string }>) {
      state.html = action.payload.html;
      state.css = action.payload.css;
      state.js = action.payload.js;
      state.imageIds = extractCmsImageIds(action.payload.html);
      state.isDirty = true;
    },
    setSlug(state, action: PayloadAction<string>) {
      state.slug = action.payload;
    },
    setTitle(state, action: PayloadAction<string>) {
      state.title = action.payload;
    },
    setTraitMode(state, action: PayloadAction<EditorTraitMode>) {
      state.traitMode = action.payload;
    },
    setLegacyDocument(
      state,
      action: PayloadAction<{ blocks: LegacyEditableDocument; html: string; css?: string; js?: string }>,
    ) {
      state.blocks = action.payload.blocks;
      state.html = action.payload.html;
      state.css = action.payload.css ?? state.css;
      state.js = action.payload.js ?? state.js;
      state.imageIds = extractCmsImageIds(action.payload.html);
      state.isDirty = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadPage.fulfilled, (state, action) => {
        if ('id' in action.payload) {
          const p = action.payload;
          state.id = p.id;
          state.slug = p.slug;
          state.title = p.title;
          state.html = p.html ?? '';
          state.css = p.css ?? '';
          state.js = p.js ?? '';
          state.blocks = p.blocks ?? null;
          state.status = p.is_published ? 'published' : 'draft';
          state.imageIds = p.image_ids ?? [];
        } else {
          state.id = undefined;
          state.slug = action.payload.slug;
          state.title = action.payload.slug;
          state.html = '';
          state.css = '';
          state.js = '';
          state.blocks = null;
          state.status = 'draft';
          state.imageIds = [];
        }
        state.isDirty = false;
        state.error = null;
      })
      .addCase(recordLoadStatus, (state, action) => {
        state.lastFetchStatus = action.payload;
      })
      .addCase(loadPage.rejected, (state, action) => {
        state.error = action.payload ?? 'Error loading page';
      })
      .addCase(savePage.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(savePage.fulfilled, (state, action) => {
        const p = action.payload;
        state.id = p.id;
        state.slug = p.slug;
        state.title = p.title;
        state.html = p.html ?? '';
        state.css = p.css ?? '';
        state.js = p.js ?? '';
        state.blocks = p.blocks ?? null;
        state.status = p.is_published ? 'published' : 'draft';
        state.imageIds = p.image_ids ?? [];
        state.isDirty = false;
        state.isSaving = false;
      })
      .addCase(savePage.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload ?? 'Error saving';
      });
  },
});

export const { setFromEditor, setSlug, setTitle, setTraitMode, setLegacyDocument } = editorSlice.actions;
export default editorSlice.reducer;

export const selectEditor = (state: RootState) => state.editor;
export const selectTraitMode = (state: RootState) => state.editor.traitMode;
